import React, {useEffect, useMemo, useState} from "react";
import {ChevronDownIcon} from "@heroicons/react/outline";
import Loader from "../loader/loader";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {fetchAllUsers, fetchUsers} from "../../../reducers/connectionReducer";
import UserInitial from "../table/user-initial";
import FormPopup from "../popup/form-popup";
import SearchIcon from "../../../resources/icons/SearchIcon";

const getComparableId = (value) => {
    if (value === undefined || value === null) {
        return null;
    }
    if (typeof value === "object") {
        return getComparableId(value.id ?? value.user?.id ?? value.value ?? null);
    }
    return String(value);
};

const getUserLabel = (user, fallback) => (
    user?.fullname
    ?? user?.name
    ?? user?.username
    ?? user?.email
    ?? fallback
);

export default function InputUserSelect({
    label = "",
    excludeId = "",
    isRequired = false,
    selectedItem = null,
    onSelect,
    onSelected,
    includeAll = true,
    setLoaded,
    selectedUsers = [],
    multiple = false,
    placeholder = "",
    size = "compact",
}) {
    const {loggedCompany} = useSelector((state) => state.user);
    const {t} = useTranslation();
    const loggedCompanyId = loggedCompany?.id ?? null;

    const dispatch = useDispatch();
    const {users, isLoadingUsers} = useSelector((state) => state.connection);

    useEffect(() => {
        if (!loggedCompanyId) {
            return;
        }
        if (includeAll) {
            dispatch(fetchAllUsers({company_id: loggedCompanyId}));
        } else {
            dispatch(fetchUsers({company_id: loggedCompanyId}));
        }
    }, [dispatch, includeAll, loggedCompanyId]);

    useEffect(() => {
        if (users.length === 0 || multiple) {
            return;
        }
        if (typeof setLoaded === "function") {
            setLoaded(false);
        }
        if (typeof onSelected === "function") {
            const selectedData = selectedItem
                ? users.find((elem) => getComparableId(elem.id) === getComparableId(selectedItem)) ?? null
                : null;
            onSelected(selectedData);
        }
    }, [multiple, onSelected, selectedItem, setLoaded, users]);

    const [query, setQuery] = useState("");
    const [isModalOpen, setModalOpen] = useState(false);
    const [modalSelection, setModalSelection] = useState(multiple ? [] : null);

    const sanitizedUsers = useMemo(() => (
        users.filter((user) => getComparableId(user.id) !== getComparableId(excludeId))
    ), [users, excludeId]);

    const filteredUsers = useMemo(() => {
        if (query.trim() === "") {
            return sanitizedUsers;
        }
        const normalizedQuery = query.toLowerCase().replace(/\s+/g, "");
        return sanitizedUsers.filter((item) => (
            getUserLabel(item, "").toLowerCase().replace(/\s+/g, "").includes(normalizedQuery)
        ));
    }, [query, sanitizedUsers]);

    const resolvedSelectedUsers = useMemo(() => {
        if (multiple) {
            const baseList = Array.isArray(selectedUsers) ? selectedUsers : [];
            const unique = new Map();
            baseList.forEach((item) => {
                const comparableId = getComparableId(item);
                if (!comparableId || comparableId === getComparableId(excludeId)) {
                    return;
                }
                const match = sanitizedUsers.find((user) => getComparableId(user.id) === comparableId) ?? item;
                if (match?.id && !unique.has(comparableId)) {
                    unique.set(comparableId, match);
                }
            });
            return Array.from(unique.values());
        }

        const comparableSelected = getComparableId(selectedItem);
        if (!comparableSelected) {
            return [];
        }
        const match = sanitizedUsers.find((user) => getComparableId(user.id) === comparableSelected);
        if (match) {
            return [match];
        }

        if (typeof selectedItem === "object" && selectedItem !== null) {
            return [selectedItem];
        }

        return [];
    }, [multiple, selectedItem, selectedUsers, sanitizedUsers, excludeId]);

    useEffect(() => {
        if (!isModalOpen) {
            return;
        }
        if (multiple) {
            setModalSelection(resolvedSelectedUsers);
        } else {
            setModalSelection(resolvedSelectedUsers[0] ?? null);
        }
    }, [isModalOpen, multiple, resolvedSelectedUsers]);

    useEffect(() => {
        if (isModalOpen) {
            setQuery("");
        }
    }, [isModalOpen]);

    const handleToggleUser = (user) => {
        if (multiple) {
            setModalSelection((prevValue) => {
                const previous = Array.isArray(prevValue) ? prevValue : [];
                const exists = previous.some((item) => getComparableId(item.id) === getComparableId(user.id));
                const nextSelection = exists
                    ? previous.filter((item) => getComparableId(item.id) !== getComparableId(user.id))
                    : [...previous, user];
                if (typeof onSelect === "function") {
                    onSelect(nextSelection);
                }
                return nextSelection;
            });
            return;
        }

        setModalSelection(user);
        if (typeof onSelect === "function") {
            onSelect(user);
        }
        setModalOpen(false);
    };

    const handleClearSelection = () => {
        if (multiple) {
            setModalSelection([]);
            if (typeof onSelect === "function") {
                onSelect([]);
            }
        } else {
            setModalSelection(null);
            if (typeof onSelect === "function") {
                onSelect(null);
            }
        }
    };

    const fieldName = typeof label === "string" && label
        ? label.toLowerCase()
        : t("app.common.value", {defaultValue: "valore"});

    const placeholderText = typeof placeholder === "string" && placeholder.trim().length > 0
        ? placeholder
        : t("app.common.placeholder", {
            field: fieldName,
            defaultValue: `Seleziona ${fieldName}`,
        });

    const displayedSelection = resolvedSelectedUsers.map((user) => getUserLabel(
        user,
        t("app.common.unknown_user", {defaultValue: "Utente sconosciuto"})
    )).join(", ");

    const hasSelection = multiple
        ? resolvedSelectedUsers.length > 0
        : resolvedSelectedUsers.length === 1;

    const selectionCount = multiple
        ? (Array.isArray(modalSelection) ? modalSelection.length : 0)
        : (modalSelection ? 1 : 0);

    const fieldMinHeight = size === "compact" ? "30px" : "44px";

    return (
        <div className="w-full">
            <label className="block text-sm font-medium mb-1 text-gray-700">
                {label} {isRequired && <span className="text-red-500">*</span>}
            </label>

            <button
                type="button"
                aria-haspopup="dialog"
                aria-expanded={isModalOpen}
                onClick={() => {
                    setModalOpen(true);
                }}
                className={`form-input flex w-full items-center justify-between gap-3 text-left text-sm transition focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isLoadingUsers ? "cursor-wait" : "cursor-pointer"
                }`}
                style={{ minHeight: fieldMinHeight }}
            >
                <span className={`flex-1 truncate ${hasSelection ? "text-gray-900" : "text-gray-400"}`}>
                    {hasSelection ? displayedSelection : placeholderText}
                </span>
                <div className="flex items-center gap-2 pl-2">
                    {hasSelection && multiple && (
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                            {resolvedSelectedUsers.length}
                        </span>
                    )}
                    {isLoadingUsers ? (
                        <Loader />
                    ) : (
                        <ChevronDownIcon
                            className="h-4 w-4 text-gray-400"
                            aria-hidden="true"
                        />
                    )}
                </div>
            </button>



            <FormPopup
                title={label || t("app.users.users", {defaultValue: "Utenti"})}
                isOpen={isModalOpen}
                setIsOpen={setModalOpen}
                defaultWith="max-w-2xl"
            >
                <div className="flex flex-col gap-4">
                    <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <SearchIcon className="h-5 w-5 text-blue-1000" aria-hidden="true" />
                        </div>
                        <input
                            type="search"
                            name="user-search"
                            id="user-search"
                            autoComplete="off"
                            className="form-search block w-full pl-10"
                            placeholder={t("app.common.search", {defaultValue: "Cerca..."})}
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                        />
                    </div>

                    {multiple && (
                        <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>
                                {t("app.common.selected_count", {
                                    count: selectionCount,
                                    defaultValue: "{{count}} selezionati",
                                })}
                            </span>
                            {selectionCount > 0 && (
                                <button
                                    type="button"
                                    className="text-xs font-medium text-red-500 hover:text-red-600"
                                    onClick={handleClearSelection}
                                >
                                    {t("app.common.clear_selection", {defaultValue: "Rimuovi selezione"})}
                                </button>
                            )}
                        </div>
                    )}

                    <div className="max-h-80 overflow-y-auto rounded-md border border-gray-200">
                        {isLoadingUsers ? (
                            <div className="flex items-center justify-center py-6">
                                <Loader />
                            </div>
                        ) : (
                            <ul className="divide-y divide-gray-100">
                                {filteredUsers.length === 0 ? (
                                    <li className="py-6 text-center text-sm text-gray-500">
                                        {t("app.common.nothing_found", {defaultValue: "Nessun risultato presente"})}
                                    </li>
                                ) : (
                                    filteredUsers.map((user) => {
                                        const comparableId = getComparableId(user.id);
                                        const isSelected = multiple
                                            ? Array.isArray(modalSelection)
                                                && modalSelection.some((item) => getComparableId(item.id) === comparableId)
                                            : getComparableId(modalSelection?.id) === comparableId;
                                        const userLabel = getUserLabel(
                                            user,
                                            t("app.common.unknown_user", {defaultValue: "Utente sconosciuto"})
                                        );

                                        return (
                                            <li key={comparableId}>
                                                <button
                                                    type="button"
                                                    className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition hover:bg-gray-100 ${
                                                        isSelected ? "bg-gray-100 font-medium text-blue-1000" : "text-gray-900"
                                                    }`}
                                                    onClick={() => handleToggleUser(user)}
                                                >
                                                    <input
                                                        type={multiple ? "checkbox" : "radio"}
                                                        readOnly
                                                        tabIndex={-1}
                                                        checked={isSelected}
                                                        className="h-4 w-4 rounded border-gray-300 text-blue-1000 focus:ring-blue-500"
                                                    />
                                                    <UserInitial user={user} />
                                                    <span className="truncate">{userLabel}</span>
                                                </button>
                                            </li>
                                        );
                                    })
                                )}
                            </ul>
                        )}
                    </div>

                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            onClick={() => setModalOpen(false)}
                        >
                            {t("app.common.close", {defaultValue: "Chiudi"})}
                        </button>
                    </div>
                </div>
            </FormPopup>
        </div>
    );
}
